import React, { useState, useEffect, useCallback, useRef, useImperativeHandle, forwardRef } from "react";
import { useLocation } from "react-router-dom";
import { LuBell } from "react-icons/lu";
import axios from "axios";
import toast from "react-hot-toast";
import { onSocketEvent, offSocketEvent, isSocketConnected } from "../../services/socket";
import NotificationPanel from "./NotificationPanel";
import "./notifications.css";

const NotificationBell = forwardRef((props, ref) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPanel, setShowPanel] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const pollRef = useRef(null);

  const location = useLocation();

  useImperativeHandle(ref, () => ({
    close: () => setShowPanel(false),
  }));

  // Close panel on route change
  useEffect(() => {
    setShowPanel(false);
  }, [location.pathname]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/v1/notifications/unread-count");
      setUnreadCount(data.count);
    } catch (err) {
      // Silent
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/v1/notifications");
      setNotifications(data.notifications);
    } catch (err) {
      // Silent
    }
  }, []);

  // Socket listener for real-time notifications
  useEffect(() => {
    const handleNotification = (notification) => {
      setUnreadCount((c) => c + 1);
      setNotifications((prev) => [notification, ...prev].slice(0, 50));
      toast(notification.title + ": " + notification.message, {
        icon: "🔔",
        duration: 5000,
      });
    };

    onSocketEvent("notification", handleNotification);
    return () => offSocketEvent("notification", handleNotification);
  }, []);

  // Fetch unread count on mount
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Graceful degradation: poll if socket not connected
  useEffect(() => {
    if (!isSocketConnected()) {
      pollRef.current = setInterval(fetchUnreadCount, 60000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchUnreadCount]);

  const handleToggle = () => {
    if (!showPanel) fetchNotifications();
    setShowPanel(!showPanel);
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await axios.put(`/api/v1/notifications/${notificationId}/read`);
      setUnreadCount((c) => Math.max(0, c - 1));
      setNotifications((prev) => prev.map((n) => n._id === notificationId ? { ...n, isRead: true } : n));
    } catch (err) {
      // Silent
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put("/api/v1/notifications/read-all");
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch (err) {
      // Silent
    }
  };

  return (
    <div className="sr__notification-bell">
      <button onClick={handleToggle} aria-label="Notifications">
        <LuBell />
        {unreadCount > 0 && (
          <span className="sr__notification-bell__badge">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>
      {showPanel && (
        <>
          <div className="sr__notification-overlay" onClick={() => setShowPanel(false)} />
          <NotificationPanel
            notifications={notifications}
            onMarkAllRead={handleMarkAllRead}
            onMarkRead={handleMarkRead}
            onClose={() => setShowPanel(false)}
          />
        </>
      )}
    </div>
  );
});

export default NotificationBell;
